import * as flags from '@fullstory/proto/fs/shared/flags';
import * as schema from './goimports/fs_shared_schema';
import * as model from './goimports/fs_model';
import * as alertmodel from './goimports/fs_services_amber_model';
import * as sharedapi from './goimports/fs_shared_sharedapi';
import * as clientapi from './goimports/fs_api_clientapi';
import * as selectors from '@fullstory/proto/fs/selectors';
import { RawEvent } from '@fullstory/recording-api';
import { ComputeElementStatsRequest } from '@fullstory/proto/fs/services/alexa';
import { int64, uint64 } from '@fullstory/proto/google/protobuf';

// It's safe to import stuff above since this is intended for our fsui client code.
// This should NEVER be referenced by the recording script (Only
// '@fullstory/recording-api' should be imported there).

// NOTE: These constants, structs, and functions must match those in clientapi.go, and schema.go.

// TODO(jh): Why isn't tsexport used to generate these definitions, so they are automatically
// kept correctly in sync?

// ---------------------------------------------------------------------------
// Structs from clientapi.go

export const INTERNAL_CREATOR = 'FullStory Helpbot';

export const HIGHLIGHTS_NOTE = 'note';

export interface NotificationsResponse {
  Notifications: ServerNotification[];
}

export enum Level {
  // Green happy bar.
  Success = 'Success',

  // Neutral blue notification bar.
  Info = 'Info',

  // Red 'stuff went wrong' bar.
  Error = 'Error',

  // Urgent notification to get the user's attention.
  Urgent = 'Urgent',

  // Give up. Party is over. Might as well reload the client.
  Fatal = 'Fatal',

  // Special gray bar with the loading spinner.
  Loading = 'Loading',

  // yellow notification bar
  Warning = 'Warning',
}

export interface ServerNotification {
  Level: keyof typeof Level;
  Msg: string;
  AutohideDelayMillis: number; // Optional. Set if our level/priority allows for auto-hiding.
  NotDismissable: boolean; // Defaults to false (meaning is Dismissable)
  Actions: NotificationAction[];
  Label: string; // Optional. Set if we need to identify this notification on the client.
}

export interface NotificationAction {
  Name: string
  Type: string   // ActionType in notification.ts
  Href: string   // Set if Type == Link
  History: string[] // Present if Type == HistoryNav.
}

export interface GetTechnologiesResponse {
  Technologies: Technology[]
  Url: string
}

export interface Technology {
  Name: string
}

export interface GoogleAuthEnforced {
  Enforced: boolean;
}

export interface SSOAuthEnforced {
  Enforced: boolean;
}

// TODO(danielwhatley): These next two structs should ideally be in a tsexport.go, but the dependencies are currently too tangled to do that.
export interface ServiceSendModal {
  Fields: { [fieldName: string]: ServiceSendModalField }
  Values: { [fieldName: string]: string }
  DisallowConfigChange: boolean;
}

export interface ServiceSendModalField {
  Desc: string;
  Type: string;
  Enum: { [key: string]: string };
  IsConfig: boolean;
  SortIndex: number;
  Required: boolean
  Children: { [key: string]: ServiceSendModalField };
}

export interface BetaFeedbackParams {
  Date: string;
  OrgId: string;
  Email: string;
  Rating: number;
  Feedback: string;
}

export interface DataExport {
  Id: number;
  Status: string;
  SpanStart: number;
  SpanEnd: number;
}

export interface SessionResponse {
  Created: number;
  UserDisplayName: string;
  UserEmail: string;
  RawUserAgent: string;
  UADevice: string;
  UAOperatingSystem: string;
  UABrowser: string;
  DeviceModel: string;
  IP: string;
  Country: string;
  Region: string;
  City: string;
  LatLong: string;
  Referrer: string;
  Pages: PageDesc[];
  SentIntegrations: { [id: string]: string };
  SessionIds: SessionId[];
  ClientIds: ClientId[];
}

export interface SessionId {
  DeviceId: string;
  SessionId: string;
}

export interface ClientId {
  DeviceUUID: string;
  SessionUUID: string;
}

export interface TrailingEventsRequest {
  UserId: string;
  SessionId: string;
  PageId: string;
  StartIdx: number;
}

export interface TrailingEventsResponse {
  Closed?: boolean;
  TrailingEvents: RawEvent[];
}

export interface TrailingPagesRequest {
  LastPageStart: number;
  SessionId: string;
  UserId: string;
}

export interface TrailingPagesResponse {
  TrailingPages: PageDesc[];
}

export interface FetchGravatarURLs {
  Emails: string[];
}

export interface FetchGravatarURLsResponse {
  EmailToLink: { [type: string]: string };
}

export interface DeadSpace {
  Start: number; // Offset from the Page Start. Relative times in Millis.
  End: number;
}

export interface PageDesc {
  UserId: string;
  SessionId: string;
  PageId: string;
  TabId: string;
  Url: string;
  Type: string;
  StartTime: number; // Absolute time in Millis of the page start.
  Duration: number;  // 0 means this page got started, but never received any events.
  Closed: boolean;
  Referrer: string;
  ScreenWidth: number;   // js screen.width
  ScreenHeight: number;  // js screen.height
  Width: number;         // initial viewport width
  Height: number;        // initial viewport width
  RawEventsUrls: string[];
  CookedPageUrls: string[];
  GcsBucketUrl: string;
  Resources: { [s: string]: string; } | null;
  DeadSpace: DeadSpace[];
  LifecycleStates: PageLifecycleTransition[];
  Notes: clientapi.Note[];
  BuildId?: string;
}

// "Page lifecycle" types from schema.proto.
export enum PageLifecycleState {
  UNKNOWN = 0,
  ACTIVE = 1,
  PASSIVE = 2,
  TERMINATED = 3,
  HIDDEN = 4,
}

export interface PageLifecycleTransition {
  start_offset?: number; // Millisecond offset from page start. 0 if absent.
  state?: PageLifecycleState; // or "UNKNOWN",
}

export interface RootResources {
  Css: string[];
  Imgs: string[];
  FetchOnlyCss?: string[];  // Not used during normal replay but can be seen in scope/bunsen
  FetchOnlyImgs?: string[]; // ...
}

export interface WebPageData {
  Url: string;
  /** @deprecated */
  Base: string;
  /** @deprecated */
  Doctype: string;
  Width: number;
  Height: number;
  /** @deprecated */
  RawEventsUrl?: string;
  RawEvents: RawEvent[];
  NextBundleSeq: number;
}

export interface RawPage {
  // Page Args
  OrgId: string;
  PageId: number;
  SessionId: number;
  UserId: number;

  // The initial Page URL.
  Url: string;

  // These were added later, so may be zero. In this case, we lose the ability to append later events.
  FromSeq: number; // Sequence number of first event bundle stored in this file (1-based)  TODO(ianrose): pointless.  remove this.
  ToSeq: number; // Sequence number of last event bundle

  Events: RawEvent[];
}

// Partial schema.CookedPage used by FSTA
export interface CookedPage {
  AppData?: schema.AppData;
  DeadSpace?: DeadSpace[];
  DeviceData?: schema.DeviceData;
  Duration?: number;
  Events?: CookedEvent[];
  Height?: number;
  IsInternalScrolling?: boolean;
  LifecycleStates?: PageLifecycleTransition[];
  Referrer?: string;
  ResourceRoots?: { [baseUrl: string]: RootResources };
  Resources?: { [s: string]: string; } | null;
  TabId?: string;
  Url?: string;
  Width?: number;
  Closed?: boolean;
}

export interface HighlightAction {
  Label: string;
  Css: string;
  Link: string;
}

export interface NewHighlight {
  // a subset of Highlight fields, what you're actually allowed to set directly:
  Id: string;    // preallocated by ShareLink, via /highlights/share
  Text: string;
  // Most often, our evidence is a session; this is true even for non-Note highlights, but
  // doubley so for notes.  So allow those fields here also:
  IndivId: string;
  UserId: string;
  SessionId: string;
  PageId: string;
  PageStart: number; // Absolute time in millis of the page start.
  When: number; // When the highlight occurs, as a ms offset from PageStart
  SessionStart: number; // Absolute time in millis of the session start.
  AbsTime: number;
  IsClip: boolean;
  Link: string;
  DevTools: sharedapi.DevToolsMode;
  ShareLink: string;
  Thumbnail: sharedapi.ThumbnailDesc;
  SendTo: string[];
}

export interface ShareRequest {
  DevTools: sharedapi.DevToolsMode;
  UserId: string;
  SessionId: string;
  PageId: string;
  PageStart: number;
  When: number;
  AbsTime: number;
  NoteId?: string; // Optional. set if an associated Note has already been saved, but not yet shared
}

export interface ShareResponse {
  UserId: string;
  ShareId: number;
  NoteId: string;  // the reserved ID for, or existing ID of, the associated Note
  ShareLinkPath: string;
}

// ---------------------------------------------------------------------------
// Structs from schema.go
//

export interface UserDetailsResponse {
  UserId: string;
  UserDisplayName: string;
  UserEmail: string;
  UserLastCity: string;
  UserLastLatLong: string;
  UserAppId: string;
  UserCreationTime: number;
  UserTotalSec: number;
  UserAvgSessionSec: number;
  UserNumEvents: number;
  UserMetadata: string;
  Clips: schema.Clip[];
  Sessions: schema.SearchSession[];
  TotalSessionCount: number;
  WatchHistory: string[];
  IntegrationUrls: { [key: string]: UserDetailLink };
}

export interface UserDetailLink {
  LinkText: string;
  LinkUrl: string;
}

export interface GraphDatum {
  Label: Date;
  Val: number;
}

// The types of clicks that could be requested for an activity map. Ideally, this would be generated by tsexport, but
// there are some complications in the server code that make that difficult atm. Needs to match schema.go.
// TODO(jaime): This must be kept in sync with schema.go. We should port to tsexport and use string enums.
// Should always be in sync with StatsTypeMap in fs/services/alexa/convert/search/convert.go
export const ToClientClickType: Map<ComputeElementStatsRequest.StatsType, string> = new Map([
  [ComputeElementStatsRequest.StatsType.STATS_TYPE_ALL_CLICKS, ''],
  [ComputeElementStatsRequest.StatsType.STATS_TYPE_DEAD_CLICKS, 'dead'],
  [ComputeElementStatsRequest.StatsType.STATS_TYPE_ERROR_CLICKS, 'error'],
  [ComputeElementStatsRequest.StatsType.STATS_TYPE_RAGE_CLICKS, 'rage'],
]);

// Kinds of cooked events.
// TODO(sethf): It would make sense to turn these into an enumeration.
export type EVENT_COOKED_LOAD_TYPE = string;
export const EVENT_COOKED_LOAD_MELTED: EVENT_COOKED_LOAD_TYPE = 'load-melted'; // Synthetic event created on the client to represent a merged load and navigate event.
export const EVENT_COOKED_LOAD: EVENT_COOKED_LOAD_TYPE = 'load';
export const EVENT_COOKED_UNLOAD = 'unload';
export const EVENT_COOKED_NAVIGATE = 'navigate';
export const EVENT_COOKED_CLICK = 'click';
export const EVENT_COOKED_TAP = 'tap';
export const EVENT_COOKED_FOCUS = 'focus';
export const EVENT_COOKED_CHANGE = 'change';
export const EVENT_COOKED_MOUSE_THRASH = 'thrash';
export const EVENT_FORM_ABANDONED = 'abandon';
export const EVENT_COOKED_ELEM_SEEN = 'seen';
export const EVENT_COOKED_REQUEST = 'request';
export const EVENT_COOKED_LONG_CLICK = 'long_click';                     // DEPRECATED - uses modifiers instead
export const EVENT_COOKED_UNHANDLED_CLICK = 'unhandled_click';           // ...
export const EVENT_COOKED_UNHANDLED_LONG_CLICK = 'unhandled_long_click'; // ...
export const EVENT_COOKED_BACKGROUNDED = 'backgrounded';
export const EVENT_COOKED_CRASHED = 'crashed';
export const EVENT_COOKED_CUSTOM = 'custom';
export const EVENT_COOKED_USERVAR = 'uservar';
export const EVENT_COOKED_PAGEVAR = 'pagevar';
export const EVENT_COOKED_CUSTOM_ERROR = 'custom_error';
export const EVENT_COOKED_USERVAR_ERROR = 'uservar_error';
export const EVENT_COOKED_PAGEVAR_ERROR = 'pagevar_error';
export const EVENT_COOKED_ELEMVAR_ERROR = 'elemvar_error';
export const EVENT_COOKED_REDACTED = 'redacted';
export const EVENT_COOKED_DATALOSS = 'dataloss';
export const EVENT_COOKED_ERROR = 'error';
export const EVENT_COOKED_CONSOLE_LOG = 'console_log';
export const EVENT_COOKED_KEYBOARD_OPEN = 'keyboard_open';
export const EVENT_COOKED_KEYBOARD_CLOSE = 'keyboard_close';
export const EVENT_COOKED_LOW_MEMORY = 'low_memory';
export const EVENT_COOKED_EXCEPTION = 'exception';
export const EVENT_COOKED_PINCH_GESTURE = 'pinch_gesture';
export const EVENT_COOKED_HIGHLIGHT = 'highlight';
export const EVENT_COOKED_PAGEVIEW = 'pageview';
export const EVENT_COOKED_COPY = 'copy';
export const EVENT_COOKED_PASTE = 'paste';
export const EVENT_COOKED_CONSOLE_MESSAGE = 'console_message';
export const EVENT_COOKED_CONSENT = 'consent';
export const EVENT_COOKED_CUMULATIVE_LAYOUT_SHIFT = 'cumulative_layout_shift';
export const EVENT_COOKED_FIRST_INPUT_DELAY = 'first_input_delay';
export const EVENT_COOKED_KEEP = 'keep';
export const EVENT_COOKED_APP_FORCED_RESTART = 'app_forced_restart';
export const EVENT_COOKED_INTERACTION_TO_NEXT_PAINT = 'interaction_to_next_paint';
export const EVENT_COOKED_SUSPICIOUS_ACTIVITY = 'suspicious_activity';

export interface CookedEvent {
  FId?: number; // Optional FrameId. Unset or 0 means outer/top window.
  PIds?: number[]; // array of parent fsId's
  When: number; // Relative time. In Millis. Offset from the start of the page.
  Type: string;

  EventCustomName?: string; // Set if Type == EVENT_COOKED_CUSTOM
  EventVars?: string;
  EventVarErrs?: EventVarErr[];

  PageTitle: string;
  PageUrl: string;

  TargetSelector?: string;
  // NamedElementId is the (datastore) id of any matching user configured Named Element.
  NamedElementId?: string;
  TargetSelectorStruct?: SelectorPart[];
  TargetText?: string;
  TargetBounds?: number[];
  TargetNthChild?: number;
  TargetTextMasked?: boolean;
  ElementVars?: string;
  ElementVarErrs?: EventVarErr[];

  DefinedEventId?: string;
  AdditionalMatchingDefinedIds?: string[];

  // TODO(jaime): This is an int64 on the server (cause I was convinced to make it such in solr, I wanted a uint32).
  // The cardinality should only ever be low thousands. We should be VERY safe against overflow in Javascript.
  ClusterId?: number;

  UserAppId?: string;
  UserDisplayName?: string;
  UserEmail?: string;
  UserMeta?: string;

  Modifiers?: { [name: string]: number };

  NumInfos?: number;
  NumWarnings?: number;
  NumErrors?: number;

  ViewId?: number;
  FsId?: number;

  LoadDomContentTime?: number;
  LoadFirstPaintTime?: number;
  LoadEventTime?: number;
  LoadLargestPaintTime?: number;
  TimeToInteractive?: number;
  TotalBlockingTime?: number;
  TimeToFirstByte?: number;

  ReqMethod?: string;
  ReqUrl?: string;
  ReqStatus?: number;
  ReqDuration?: number;

  // Only when Type == EVENT_COOKED_ERROR
  ErrorTotalNum?: number;
  ErrorType?: string;
  ErrorMessage?: string;

  Redacted?: boolean;

  PageDefId?: string;
  PageVars?: string;
  IsUnset?: boolean;

  // Metadata bitfield that can be present on any cooked event
  EventDiagnosticMetadata?: number;

  // Only when Type == EVENT_COOKED_NAVIGATE
  NavigateDetails?: EventNavigateDetails;
  // Only when Type == EVENT_COOKED_LOW_MEMORY
  LowMemoryDetails?: EventLowMemoryDetails;
  // Only when Type == EVENT_COOKED_EXCEPTION
  ExceptionDetails?: EventExceptionDetails;
  // Only when Type == EVENT_COOKED_PINCH_GESTURE
  PinchGestureDetails?: PinchGestureDetails;
  // Only when Type == EVENT_COOKED_DATALOSS
  DatalossDetails?: DatalossDetails;
  // Only when Type == EVENT_COOKED_ELEM_SEEN
  WatchedElementDetails?: EventWatchedElementDetails;
  // Only when Type == EVENT_COOKED_PAGEVIEW
  PageViewDetails?: EventPageViewDetails;
  // Only when Type == EVENT_COOKED_CONSOLE_MESSAGE
  ConsoleMessageDetails?: EventConsoleMessageDetails;
  // Only when Type == EVENT_COOKED_CUMULATIVE_LAYOUT_SHIFT
  CumulativeLayoutShiftDetails?: EventCumulativeLayoutShiftDetails;
  // Only when Type == EVENT_COOKED_FIRST_INPUT_DELAY
  FirstInputDelayDetails?: EventFirstInputDelayDetails;
  // Only when Type == EVENT_COOKED_KEEP
  KeepDetails?: EventKeepDetails;
  // Only when Type == EVENT_COOKED_APP_FORCED_RESTART
  AppForcedRestartDetails?: EventAppForcedRestartDetails;
  // Only when Type == EVENT_COOKED_INTERACTION_TO_NEXT_PAINT
  InteractionToNextPaintDetails?: EventInteractionToNextPaintDetails;
}

export interface SelectorPart {
  Tag: string;
  Id: string;
  Classes: string[];
  Attrs: { [attr: string]: string };
}

export function ToSelectorPartProtos(parts: SelectorPart[]): selectors.SelectorPart[] {
  return parts.map(ToSelectorPartProto);
}

export function ToSelectorPartProto(part: SelectorPart): selectors.SelectorPart {
  return {
    tag: part.Tag,
    id: part.Id,
    classes: part.Classes,
    attrs: part.Attrs,
    attrMatch: {},
    pseudoClasses: [],
  };
}

export interface PasswordResetRequestParams {
  Username: string;
}

export interface UpdateDataExportFreqReq {
  FrequencyMinutes: number
}

export interface ShareLinkSignupReq {
  Email: string;
  Path: string; // The share link path the user was on. For example: "/v/x1y2z3"
}

// Company types
export const ECommerceIndustry = 'e-commerce';
export const TravelIndustry = 'travel';
export const SaaSIndustry = 'saas';
export const FinancialIndustry = 'financial';
export const GamingIndustry = 'gaming';
export const HealthIndustry = 'health';
export const MediaIndustry = 'media';
export const AgencyIndustry = 'agency';
export const DeprecatedB2BIndustry = 'b2b';
export const DeprecatedB2CIndustry = 'b2c';

type AlertSettings = alertmodel.DailyActivesThresholdSettings; // Union this with more alert settings types as we add them.

export interface Alert {
  CreatedBy: string;
  CreatedAt: number;
  Description: string;
  Type: alertmodel.AlertType;
  Settings: AlertSettings;
  NotificationSettings: AlertNotificationSetting[];
}

export interface AlertAndKey {
  Alert: Alert;
  Key: alertmodel.AlertKey;
}

export interface AlertNotificationAndKey {
  Notification: AlertNotification;
  Key: alertmodel.AlertNotificationKey;
}

export interface AlertNotification {
  CreatedAt: number;
  OccurredAt: number;
  Alert: Alert;
}

export interface AlertNotificationSetting {
  Medium: alertmodel.NotificationMedium;
  Destination: string;
  Cooldown: number; // time.Duration (nanoseconds)
  LastTriggered: number; // schema.TimeMs
}

export interface AlertsResponse {
  Alerts: AlertAndKey[];
}

export interface AlertNotificationsRequest {
  AlertKey?: alertmodel.AlertKey;
  Limit: number;
}

export interface AlertNotificationsResponse {
  Notifications: AlertNotificationAndKey[];
}

export interface GetAlertsRequest {
  SegmentId: string;
  SearchieFlag?: flags.Flag;
}

export interface NewAlertRequest {
  SegmentId: string;
  SearchieFlag: number;
  Type: alertmodel.AlertType;
  Description: string;
  NotificationSettings: AlertNotificationSetting[];
  Settings: AlertSettings;
}

export interface UpdateAlertRequest {
  Key: alertmodel.AlertKey;
  Description: string;
  NotificationSettings: AlertNotificationSetting[];
  Settings: AlertSettings;
}

export interface DeleteAlertRequest {
  Key: alertmodel.AlertKey;
}

export interface EventVarErr {
  ErrCode: number,
  Field: string,
  Value: string,
}

export interface DateRange {
  id: model.DateRangeID;
  name: string;
  range: schema.TimeRange;
}

export enum NavigationReason {
  NAVIGATION_REASON_UNKNOWN = 0,
  NAVIGATION_REASON_NAVIGATE = 1,
  NAVIGATION_REASON_RELOAD = 2,
  NAVIGATION_REASON_BACK_FORWARD = 3,
  NAVIGATION_REASON_PRERENDER = 4,
}

export interface EventNavigateDetails {
  Reason: NavigationReason;
}

export interface EventLowMemoryDetails {
  AvailableMemory: number;
  MaxMemory: number;
}

export interface EventExceptionDetails {
  SourceFile: string;
  Count: number;
  Caught: boolean;
}

export enum PinchGestureType {
  PINCH_GESTURE_INVLAID = 0,
  PINCH_GESTURE_SCALE_IN = 1,
  PINCH_GESTURE_SCALE_OUT = 2,
}

export interface PinchGestureDetails {
  PinchGestureType: PinchGestureType;
}

export enum DatalossType {
  DATALOSS_TYPE_NONE = 0,
  DATALOSS_TYPE_PAYMENT_CARD = 1,
  DATALOSS_TYPE_EMAIL = 2,
  DATALOSS_TYPE_SSN = 3,
  DATALOSS_TYPE_PASSWORD = 4,
}

export enum DatalossSource {
  DATALOSS_SOURCE_UNKNOWN = 0,
  DATALOSS_SOURCE_ELEMENT = 1,
  DATALOSS_SOURCE_INPUT_ELEMENT = 2,
  DATALOSS_SOURCE_NAVIGATE_URL = 3,
  DATALOSS_SOURCE_NETWORK_URL = 4,
  DATALOSS_SOURCE_NETWORK_REQUEST_HEADER = 5,
  DATALOSS_SOURCE_NETWORK_RESPONSE_HEADER = 6,
  DATALOSS_SOURCE_NETWORK_REQUEST_BODY = 7,
  DATALOSS_SOURCE_NETWORK_RESPONSE_BODY = 8,
  DATALOSS_SOURCE_EXCEPTION = 9,
  DATALOSS_SOURCE_LOG = 10,
}

export interface DatalossDetails {
  Type: DatalossType;
  Source: DatalossSource;
  Origin: string;
  RequestUrl: string;
}

export enum WatchedElementType {
  WATCHED_ELEMENT_TYPE_UNKNOWN = 0,
  WATCHED_ELEMENT_TYPE_RENDERED = 1,
  WATCHED_ELEMENT_TYPE_VISIBLE = 2,
  WATCHED_ELEMENT_TYPE_END = 3,
}

export interface EventWatchedElementDetails {
  Type: WatchedElementType;
  RenderDuration: number;
  VisibleDuration: number;
  NodeId: number;
  StartTime: number;
  StartType: WatchedElementType;
}

export interface EventPageViewDetails {
  StartTime: int64;
  EndTime: int64;
  PageName: string;
};

export enum ConsoleMessageLevelType {
  CONSOLE_MESSAGE_LEVEL_TYPE_UNKNOWN = 0,
  CONSOLE_MESSAGE_LEVEL_TYPE_ERROR = 1,
}

export interface EventConsoleMessageDetails {
  Type: ConsoleMessageLevelType;
}

export interface EventFirstInputDelayDetails {
  Delay: number;
}

export interface EventCumulativeLayoutShiftDetails {
  Score: number;
}

export interface EventInteractionToNextPaintDetails {
  Timing: number;
  EventName: string;
}

export enum KeepRuleType {
  KEEP_RULE_INVALID = 0,
  KEEP_RULE_ELEMENT = 1,
  KEEP_RULE_URL = 2,
  KEEP_RULE_DWELL = 3,
}
export interface EventKeepDetails {
  Type: KeepRuleType;
}

export interface EventAppForcedRestartDetails {
  OrgId: string;
  UserId: string;
  SessionId: string;
  PageId: string;
  HasClientsideIds: boolean;
  ElapsedMs: uint64;
}

export enum SuspiciousActivityType {
  SUSPICIOUS_ACTIVITY_TYPE_INVALID = 0,
  SUSPICIOUS_ACTIVITY_TYPE_FRAUD = 1,
  SUSPICIOUS_ACTIVITY_TYPE_AUTOMATION = 2,
}

export enum SuspiciousActivityCategory {
  SUSPICIOUS_ACTIVITY_CATEGORY_INVALID = 0,
  SUSPICIOUS_ACTIVITY_CATEGORY_SQL_INJECTION = 1,
  SUSPICIOUS_ACTIVITY_CATEGORY_XSS = 2,
}

export interface EventSuspiciousActivityDetails {
  Type: SuspiciousActivityType;
  Category: SuspiciousActivityCategory;
}
